"use client";

import {
  createContext,
  useState,
  useEffect,
  type ReactNode,
  useContext,
  useCallback,
  type KeyboardEvent,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type {
  CarouselApi,
  CarouselContextProps,
} from "@/types/components/carousel";

const CarouselContext = createContext<
  | (CarouselContextProps & {
      handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    })
  | null
>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function CarouselProvider({ children }: { children: ReactNode }) {
  // - * - * States * - * - //
  const { opts, plugins, setApi, orientation } =
    useContext(CarouselContext) ?? {};

  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // - * - * Handlers * - * - //

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  // - * - * Constants * - * - //

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  // - * - * Effects * - * - //

  useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        handleKeyDown,
      }}
    >
      {children}
    </CarouselContext.Provider>
  );
}

export { useCarousel };
export default CarouselProvider;
